import type { Api } from '../api.js';
import { texts, useLang } from '../i18n.js';
import { RowMenu, toast } from '../ui.js';

/**
 * Hide a project from your own My work (owner 2026-10-20: e.g. a client who has not paid). Personal only: the project and
 * everyone else's lists stay as they are. Hidden projects are listed under the lists so they can come back.
 */
export interface HiddenProject {
  projectId: string;
  projectName: string;
  client: string;
}

const TX = texts(
  {
    hide: 'Hide',
    hideProject: 'Hide the project from My work',
    more: (name: string) => `More for ${name}`,
    hideLabel: (name: string) => `Hide ${name} from My work`,
    hidden: (name: string) => `${name} is hidden from My work`,
    shown: (name: string) => `${name} is back in My work`,
    hiddenList: 'Hidden projects:',
    show: 'Show again',
    showLabel: (name: string) => `Show ${name} in My work again`
  },
  {
    hide: 'Dölj',
    hideProject: 'Dölj projektet från Mitt arbete',
    more: (name: string) => `Mer för ${name}`,
    hideLabel: (name: string) => `Dölj ${name} från Mitt arbete`,
    hidden: (name: string) => `${name} är dolt från Mitt arbete`,
    shown: (name: string) => `${name} syns i Mitt arbete igen`,
    hiddenList: 'Dolda projekt:',
    show: 'Visa igen',
    showLabel: (name: string) => `Visa ${name} i Mitt arbete igen`
  }
);

export async function setProjectHidden(api: Api, projectId: string, hidden: boolean) {
  await api.put(`/api/me/hidden-projects/${projectId}`, { hidden });
}

/** "Dölj" on a row, in its ⋯ (owner 2026-11-01: a calmer row): hides the whole project (all its videos), with Undo. */
export function HideProject(p: { api: Api; projectId: string; projectName: string; done: () => void }) {
  const t = TX[useLang()];
  const run = async () => {
    try {
      await setProjectHidden(p.api, p.projectId, true);
      p.done();
      toast({
        text: t.hidden(p.projectName),
        undo: async () => {
          await setProjectHidden(p.api, p.projectId, false);
          p.done();
        }
      });
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    }
  };
  return (
    <span class="mw-rowmenu">
      <RowMenu label={t.more(p.projectName)} items={[{ label: t.hideProject, run: () => void run() }]} />
    </span>
  );
}

/** One quiet line under the lists: the projects you hid, each with "Visa igen". */
export function HiddenProjects(p: { api: Api; hidden: HiddenProject[] | undefined; done: () => void }) {
  const t = TX[useLang()];
  if (!p.hidden?.length) return null;
  const show = async (h: HiddenProject) => {
    try {
      await setProjectHidden(p.api, h.projectId, false);
      p.done();
      toast({ text: t.shown(h.projectName) });
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    }
  };
  return (
    <p class="small muted mw-hidden">
      {t.hiddenList}{' '}
      {p.hidden.map((h, i) => (
        <span key={h.projectId}>
          {i ? ' · ' : null}
          {h.client} · {h.projectName}{' '}
          <button type="button" class="td-link mw-unhide" aria-label={t.showLabel(h.projectName)} onClick={() => show(h)}>
            {t.show}
          </button>
        </span>
      ))}
    </p>
  );
}
